import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Truck, Package, FileText, Users, Shield, LogOut, BookOpen, Scan, MessageSquare, Receipt, FolderOpen, SearchCheck, BarChart3, CreditCard, UserCircle, Landmark, ShieldCheck, DollarSign, TrendingUp, Brain, ClipboardCheck, Zap, Bell } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';

export default function Layout({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const [emailPrefPhase3, setEmailPrefPhase3] = useState(true);
  const [prefSaving, setPrefSaving] = useState(false);
  const [myScorecard, setMyScorecard] = useState(null);

  useEffect(() => {
    if (!user?.training_mode) { setMyScorecard(null); return; }
    const fetchScore = () => {
      api.get('/training/scorecard/me')
        .then(res => setMyScorecard(res.data))
        .catch(() => {});
    };
    fetchScore();
    const id = setInterval(fetchScore, 12000);
    return () => clearInterval(id);
  }, [user?.training_mode, user?.id]);

  useEffect(() => {
    const fetchUnread = () => {
      api.get('/messages/unread-count')
        .then(res => setUnreadMessages(res.data.count || 0))
        .catch(() => {});
    };
    const fetchNotifications = () => {
      api.get('/notifications')
        .then(res => setNotifications(res.data || []))
        .catch(() => {});
    };
    fetchUnread();
    fetchNotifications();
    // Load notification preferences once
    api.get('/users/me/notification-preferences')
      .then(res => setEmailPrefPhase3(res.data.email_notifications_phase3 ?? true))
      .catch(() => {});
    const interval = setInterval(() => {
      fetchUnread();
      fetchNotifications();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const markNotifRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { /* silent */ }
  };

  const openNotifPanel = (open) => {
    setNotifOpen(open);
    if (open) {
      // Mark all as read after 1s so users see the badge count first
      setTimeout(() => {
        notifications.filter(n => !n.is_read).forEach(n => markNotifRead(n.id));
      }, 1000);
    }
  };

  const toggleEmailPrefPhase3 = async () => {
    const next = !emailPrefPhase3;
    setEmailPrefPhase3(next);
    setPrefSaving(true);
    try {
      await api.put('/users/me/notification-preferences', { email_notifications_phase3: next });
      toast.success(next ? 'Phase 3 email pings ON' : 'Phase 3 email pings muted');
    } catch (err) {
      setEmailPrefPhase3(!next);
      toast.error(err.response?.data?.detail || 'Failed to save preference');
    } finally {
      setPrefSaving(false);
    }
  };

  const handleLogout = () => {
    api.post('/auth/logout')
      .then(() => {
        setUser(null);
        toast.success('Logged out successfully');
        navigate('/auth');
      })
      .catch(() => toast.error('Logout failed'));
  };

  const navLinks = [
    { path: '/super-admin', icon: Shield, label: 'Super Admin', superAdminOnly: true },
    { path: '/payments', icon: CreditCard, label: 'Payments', superAdminOnly: true },
    { path: '/folders', icon: FolderOpen, label: 'Paperwork', brokerOnly: true },
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/carriers', icon: Truck, label: 'Carriers' },
    { path: '/shippers', icon: Package, label: 'Brokers' },
    { path: '/loads', icon: FileText, label: 'Loads' },
    { path: '/drivers', icon: UserCircle, label: 'Drivers' },
    { path: '/invoices', icon: Receipt, label: 'Invoices' },
    { path: '/settlements', icon: Landmark, label: 'Settlements' },
    { path: '/my-earnings', icon: DollarSign, label: 'My Earnings' },
    ...(user?.role === 'super_admin' || user?.role === 'broker' ? [{ path: '/revenue', icon: TrendingUp, label: 'Revenue Board' }] : []),
    { path: '/compliance', icon: ShieldCheck, label: 'Compliance' },
    { path: '/credit-check', icon: SearchCheck, label: 'Credit Check' },
    { path: '/ai-dispatch', icon: Brain, label: 'AI Dispatch' },
    ...(user?.ai_mode === 'autonomous' ? [{ path: '/ai-roundtrip', icon: Zap, label: 'Roundtrip Dispatch' }] : []),
    ...((user?.role === 'broker' || user?.role === 'super_admin' || (user?.role === 'broker_manager' && user?.ai_mode === 'autonomous')) ? [{ path: '/fleet-rates', icon: Truck, label: 'Fleet Rates' }] : []),
    ...(user?.role === 'super_admin' || user?.role === 'broker' || user?.role === 'broker_manager' ? [{ path: '/load-approvals', icon: ClipboardCheck, label: 'Load Approvals' }] : []),
    ...(user?.role === 'super_admin' || user?.role === 'broker' ? [{ path: '/analytics', icon: BarChart3, label: 'Analytics' }] : []),
    { path: '/documents', icon: Scan, label: 'AI Scanner' },
    { path: '/messages', icon: MessageSquare, label: 'Messages' },
    { path: '/agents', icon: Users, label: 'User Management', brokerOnly: true },
    { path: '/training', icon: BookOpen, label: 'Training' },
    { path: '/profile', icon: UserCircle, label: 'My Profile' }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="main-layout">
      {/* Training Mode banner — persistent when user.training_mode = true */}
      {user?.training_mode && (
        <div
          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2 px-4 text-xs sm:text-sm font-semibold sticky top-0 z-[60] shadow"
          data-testid="training-mode-banner"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <span>🎓 TRAINING MODE — no real emails/bookings. Practice freely.</span>
            {myScorecard && (
              <div className="flex items-center gap-2" data-testid="banner-scorecard">
                <span className="text-white/90">
                  {myScorecard.successful_count} / {myScorecard.graduation_threshold} successful
                </span>
                <div className="w-24 sm:w-32 h-1.5 bg-white/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white transition-all"
                    style={{ width: `${myScorecard.progress_pct}%` }}
                    data-testid="banner-progress"
                  />
                </div>
                <span className="text-white/90 hidden sm:inline">
                  {myScorecard.seats_left} to graduate · {myScorecard.accuracy}% acc
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">{user.organization_name || 'Urbanvision Logistics'}</h1>
                <p className="text-xs text-slate-500">LLC</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Notifications Bell */}
              <Popover open={notifOpen} onOpenChange={openNotifPanel}>
                <PopoverTrigger asChild>
                  <button
                    className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid="notifications-bell"
                    aria-label="Notifications"
                  >
                    <Bell className="w-5 h-5 text-slate-700" />
                    {unreadCount > 0 && (
                      <span
                        className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                        data-testid="notif-badge"
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-96 p-0"
                  align="end"
                  data-testid="notif-panel"
                >
                  <div className="p-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-900">Notifications</p>
                    <p className="text-xs text-slate-500">{notifications.length === 0 ? 'You\'re all caught up' : `${unreadCount} unread`}</p>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-sm text-slate-400">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.slice(0, 25).map(n => (
                        <div
                          key={n.id}
                          className={`p-3 border-b border-slate-50 hover:bg-slate-50 ${!n.is_read ? 'bg-amber-50/40' : ''}`}
                          data-testid={`notif-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            {n.type === 'phase3_granted' && (
                              <div className="bg-amber-500/20 rounded-full p-1 mt-0.5">
                                <Zap className="w-3 h-3 text-amber-600" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-800">{n.message}</p>
                              <p className="text-[11px] text-slate-400 mt-0.5">
                                {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                              </p>
                            </div>
                            {!n.is_read && (
                              <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Notification preferences */}
                  <div className="p-3 border-t border-slate-100 bg-slate-50/60">
                    <label
                      className="flex items-center justify-between gap-3 cursor-pointer"
                      data-testid="notif-pref-phase3-toggle"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-700">Phase 3 grant emails</p>
                        <p className="text-[10px] text-slate-500">In-app bell always fires. Emails optional.</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={emailPrefPhase3}
                        onClick={toggleEmailPrefPhase3}
                        disabled={prefSaving}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${emailPrefPhase3 ? 'bg-amber-500' : 'bg-slate-300'} ${prefSaving ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid="notif-pref-phase3-switch"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${emailPrefPhase3 ? 'translate-x-4' : 'translate-x-0.5'}`}
                        />
                      </button>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                {user.pin_code && (
                  <p className="text-xs font-bold text-blue-600">Agent ID: {user.pin_code}</p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-4rem)] sticky top-16">
          <nav className="p-4 space-y-2">
            {navLinks.map(({ path, icon: Icon, label, brokerOnly, superAdminOnly }) => {
              // Hide super admin links from non-super admins
              if (superAdminOnly && user.role !== 'super_admin') return null;
              // Hide broker-only links from agents (but super_admin can see everything)
              if (brokerOnly && user.role === 'agent') return null;
              
              const isActive = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                  {label === 'Messages' && unreadMessages > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-testid="unread-badge">{unreadMessages}</span>
                  )}
                  {brokerOnly && user.role !== 'super_admin' && label !== 'Messages' && <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Broker</span>}
                  {superAdminOnly && <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Admin</span>}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
